"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import Script from "next/script";

interface GoogleMapsContextType {
  isLoaded: boolean;
  error: string | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  error: null,
});

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}

export function GoogleMapsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasApiKey] = useState(() => {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error("Google Maps API key is not set");
      return false;
    }
    return true;
  });

  if (!hasApiKey) {
    return <>{children}</>;
  }

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, error }}>
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places`}
        strategy="afterInteractive"
        onReady={() => {
          console.log("Google Maps script is ready to load");
        }}
        onLoad={() => {
          console.log("Google Maps script loaded successfully");
          if (window.google?.maps) {
            console.log("Google Maps object is available");
            setIsLoaded(true);
            // Dispatch event for legacy compatibility
            const event = new CustomEvent("google-maps-loaded", {
              bubbles: true,
            });
            window.dispatchEvent(event);
          } else {
            console.error("Google Maps object is not available after load");
            setError("Google Maps failed to initialize");
          }
        }}
        onError={(e) => {
          console.error("Error loading Google Maps script:", e);
          setError("Failed to load Google Maps script");
        }}
      />
      {children}
    </GoogleMapsContext.Provider>
  );
}
